import React from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { Plus, ExternalLink, Edit2, Trash2, RefreshCw } from 'lucide-react';

const Container = styled.div`
  width: 100%;
  height: 100%;
  background: #f9fafb;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 24px 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  border-bottom: 1px solid #f3f4f6;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  font-family: 'Space Grotesk', sans-serif;
`;

const AddButton = styled.button`
  background: #000000;
  color: #ffffff;
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const Grid = styled.div`
  padding: 24px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  overflow-y: auto;
  align-content: start;
`;

const Card = styled(motion.div)`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const BotName = styled.h3`
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 4px 0;
`;

const BotID = styled.div`
  font-size: 10px;
  color: #9ca3af;
  font-family: monospace;
`;

const ChartArea = styled.div`
  height: 80px;
  width: 100%;
  margin: 4px 0;
`;

const Sparkline = styled.svg`
  width: 100%;
  height: 100%;
  overflow: visible;
  
  path.line {
    stroke: #22c55e;
    stroke-width: 2;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  
  path.area {
    fill: rgba(34, 197, 94, 0.1);
    stroke: none;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: auto;
`;

const ActionButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px;
  border: 1px solid #f3f4f6;
  background: #f9fafb;
  border-radius: 6px;
  color: #4b5563;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #ffffff;
    color: #111827;
    border-color: #e5e7eb;
    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  }
`;

const bots = [
  { name: 'Ellie', id: '371c3f57-8d53-4c3c', data: [20, 40, 35, 50, 45, 60, 55, 70, 65, 80] },
  { name: 'فهمة', id: '4472b906-512c-46c4', data: [10, 10, 10, 10, 15, 10, 10, 80, 90, 85] },
  { name: 'Freddie Follicle', id: '85f88bb2-9449-4533', data: [30, 45, 40, 55, 50, 65, 60, 55, 70, 65] },
  { name: 'Chris', id: 'a2036387-128f-4035', data: [25, 30, 25, 35, 30, 40, 35, 30, 45, 40] },
];

const MultiBrandVisual: React.FC = () => {
  // Generate path for line
  const getLinePath = (data: number[]) => {
    const max = 100; // Fixed max for consistency
    const points = data.map((val, i) => {
      const x = (i / (data.length - 1)) * 100;
      const y = 100 - (val / max) * 100;
      return `${x},${y}`;
    });
    return `M${points[0]} L${points.join(' L')}`;
  };

  // Generate path for area (closed loop)
  const getAreaPath = (data: number[]) => {
    const linePath = getLinePath(data);
    return `${linePath} L100,100 L0,100 Z`;
  };

  return (
    <Container>
      <Header>
        <Title>My Chatbots</Title>
        <AddButton>
          <Plus size={14} />
          Add New Chatbot
        </AddButton>
      </Header>
      <Grid>
        {bots.map((bot, index) => (
          <Card
            key={bot.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1 }}
          >
            <CardHeader>
              <div>
                <BotName>{bot.name}</BotName>
                <BotID>ID: {bot.id}</BotID>
              </div>
              <RefreshCw size={14} color="#9ca3af" />
            </CardHeader>
            <ChartArea>
              <Sparkline viewBox="0 0 100 100" preserveAspectRatio="none">
                <motion.path
                  className="area"
                  d={getAreaPath(bot.data)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1, delay: index * 0.1 }}
                />
                <motion.path
                  className="line"
                  d={getLinePath(bot.data)}
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1.5, delay: index * 0.1 }}
                />
              </Sparkline>
            </ChartArea>
            <Actions>
              <ActionButton><ExternalLink size={12} /> Open</ActionButton>
              <ActionButton><Edit2 size={12} /> Edit</ActionButton>
              <ActionButton><Trash2 size={12} /> Delete</ActionButton>
            </Actions>
          </Card>
        ))}
      </Grid>
    </Container>
  );
};

export default MultiBrandVisual;
